import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Trash2, Pencil } from "lucide-react";

const EMPTY = {
  name: "", category: "", description: "", model_number: "", spec: "",
  unit: "each", pack_size: 1, origin: "Canada", lead_time_days: 7,
  quantity_available: 1, price_hint: "",
};

export default function Products() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null); // product id when editing
  const [err, setErr] = useState("");
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = () => api.get("/products?mine=true").then(r => setRows(r.data));
  useEffect(() => { load(); api.get("/meta/categories").then(r => setCats(r.data)); }, []);

  const reset = () => { setForm(EMPTY); setEditing(null); setShow(false); setErr(""); };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const payload = {
      ...form,
      pack_size: +form.pack_size,
      lead_time_days: +form.lead_time_days,
      quantity_available: +form.quantity_available,
      price_hint: form.price_hint === "" || form.price_hint == null ? null : +form.price_hint,
    };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      reset();
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || "", category: p.category || "", description: p.description || "",
      model_number: p.model_number || "", spec: p.spec || "",
      unit: p.unit || "each", pack_size: p.pack_size ?? 1, origin: p.origin || "Canada",
      lead_time_days: p.lead_time_days ?? 7,
      quantity_available: p.quantity_available ?? 0,
      price_hint: p.price_hint == null ? "" : p.price_hint,
    });
    setShow(true);
    setErr("");
  };

  const del = async (id) => { if (!window.confirm("Delete product?")) return; await api.delete(`/products/${id}`); load(); };

  const fmtPrice = (v) => (v == null || v === "" || isNaN(+v)) ? "—" : `$${(+v).toFixed(2)}`;
  const stockStyle = (q) => ({ color: (q ?? 0) > 0 ? "var(--ink)" : "var(--red)", fontWeight: (q ?? 0) > 0 ? 400 : 600 });

  return (
    <>
      <PageHeader
        title="My Products"
        subtitle="Structured product listings with current stock and price hints make your company eligible for buyer RFQs."
        actions={
          <button className="btn btn-primary" data-testid="add-product"
                  onClick={() => { setEditing(null); setForm(EMPTY); setShow(true); setErr(""); }}>
            + Add Product
          </button>
        }
      />
      {show && (
        <form onSubmit={submit} className="card" style={{ padding: 24, marginBottom: 24, display: "grid", gap: 12 }}>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>
            {editing ? "EDIT PRODUCT" : "NEW PRODUCT"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div><label className="label">Name *</label><input className="input" data-testid="product-name" required value={form.name} onChange={e => set("name", e.target.value)} /></div>
            <div><label className="label">Category *</label>
              <select className="input" data-testid="product-category" required value={form.category} onChange={e => set("category", e.target.value)}>
                <option value="">Select…</option>{cats.map(c => <option key={c}>{c}</option>)}
              </select></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 12 }}>
            <div><label className="label">Model #</label><input className="input" value={form.model_number} onChange={e => set("model_number", e.target.value)} /></div>
            <div><label className="label">Unit</label><input className="input" value={form.unit} onChange={e => set("unit", e.target.value)} /></div>
            <div><label className="label">Pack</label><input className="input" type="number" min="1" value={form.pack_size} onChange={e => set("pack_size", e.target.value)} /></div>
            <div><label className="label">Lead (days)</label><input className="input" type="number" min="1" value={form.lead_time_days} onChange={e => set("lead_time_days", e.target.value)} /></div>
            <div>
              <label className="label">Stock *</label>
              <input className="input" type="number" min="0" data-testid="product-stock" value={form.quantity_available} onChange={e => set("quantity_available", e.target.value)} />
            </div>
            <div>
              <label className="label">Price hint (CAD)</label>
              <input className="input" type="number" step="0.01" min="0" placeholder="e.g. 12.50" data-testid="product-price-hint" value={form.price_hint} onChange={e => set("price_hint", e.target.value)} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-mute)", marginTop: -4 }}>
            Stock must be greater than 0 for this product to make your company eligible for matching RFQs in this category.
          </div>
          <div><label className="label">Description</label><textarea className="input" style={{ minHeight: 60 }} value={form.description} onChange={e => set("description", e.target.value)} /></div>
          {err && <div style={{ color: "var(--red)" }} data-testid="product-form-error">{err}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary" data-testid="product-save">{editing ? "Save changes" : "Save"}</button>
            <button type="button" className="btn btn-ghost" onClick={reset}>Cancel</button>
          </div>
        </form>
      )}
      <div className="card">
        {rows.length === 0 ? <div style={{ padding: 32, textAlign: "center", color: "var(--ink-mute)" }}>No products yet.</div> : (
          <table className="data">
            <thead><tr><th>Name</th><th>Model</th><th>Category</th><th>Stock</th><th>Lead</th><th>Price Hint</th><th></th></tr></thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id} data-testid={`product-row-${p.id}`}>
                  <td>{p.name}</td>
                  <td className="font-mono" style={{ color: "var(--amber-400)" }}>{p.model_number || "—"}</td>
                  <td style={{ color: "var(--ink-dim)", fontSize: 13 }}>{p.category}</td>
                  <td style={stockStyle(p.quantity_available)} data-testid={`product-stock-${p.id}`}>{p.quantity_available ?? 0}</td>
                  <td>{p.lead_time_days}d</td>
                  <td data-testid={`product-price-${p.id}`}>{fmtPrice(p.price_hint)}</td>
                  <td style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button className="btn btn-ghost" data-testid={`product-edit-${p.id}`} onClick={() => startEdit(p)} style={{ padding: "6px 10px" }}><Pencil size={14} /></button>
                    <button className="btn btn-danger" data-testid={`product-delete-${p.id}`} onClick={() => del(p.id)} style={{ padding: "6px 10px" }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
